// Upload purposes: what may be stored, where, how big and by whom. Pure data, imported
// by the server (policy), the panel and the site (accept list, size hints) alike; the
// S3 client itself lives in ./storage and never reaches a browser bundle.

import type { SectionKey } from "@car/auth/roles";

const MiB = 1024 * 1024;

export const uploadPurposeKeys = [
  "site-image",
  "site-video",
  "document-file",
  "work-order-photo",
  "order-document",
  "contract-file",
  "ticket-file",
  "ticket-staff-file",
  "payment-receipt",
  "staff-payment-receipt",
  "proforma-file",
  "team-file",
  "live-chat-file",
  "live-chat-staff-file",
  "shipment-file",
  "parts-photo",
  "staff-signature",
] as const;
export type UploadPurpose = (typeof uploadPurposeKeys)[number];

/** Condition photos one work order keeps (admission and workshop page alike) */
export const MAX_WORK_ORDER_PHOTOS = 12;

export type UploadPolicy = {
  /** public: a plain URL anyone can open; private: short-lived signed links only */
  visibility: "public" | "private";
  /**
   * who may upload: a signed-in customer, any staff member, a website visitor through their
   * live chat (no account; see chat.visitor.createUpload), or staff whose role holds one of these sections
   */
  uploader: "customer" | "staff" | "visitor" | readonly SectionKey[];
  /** first key segment in the bucket */
  folder: string;
  types: readonly string[];
  maxBytes: number;
  /** allowed types as a person reads them, for hints and refusals */
  typesLabel: string;
};

export const uploadPolicies = {
  // vehicle photos, gallery, post covers, banners
  "site-image": {
    visibility: "public",
    uploader: ["content"],
    folder: "site/images",
    types: ["image/jpeg", "image/png", "image/webp"],
    maxBytes: 15 * MiB,
    typesLabel: "JPG، PNG یا WebP",
  },
  // served as uploaded (no transcoding), so the cap keeps visitors' downloads sane
  "site-video": {
    visibility: "public",
    uploader: ["content"],
    folder: "site/videos",
    types: ["video/mp4", "video/webm"],
    maxBytes: 500 * MiB,
    typesLabel: "MP4 یا WebM",
  },
  // a customer's scans and photos kept with an Owned Vehicle
  "document-file": {
    visibility: "private",
    uploader: "customer",
    folder: "documents",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 10 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // a vehicle's condition at reception: private, seen only by staff and its owner
  "work-order-photo": {
    visibility: "private",
    uploader: ["reception", "workshop"],
    folder: "work-orders",
    types: ["image/jpeg", "image/png", "image/webp"],
    maxBytes: 10 * MiB,
    typesLabel: "JPG، PNG یا WebP",
  },
  // an imported-vehicle order's paperwork, filed per stage by sales staff; multi-page
  // customs and shipping scans run larger than a customer's own documents
  "order-document": {
    visibility: "private",
    uploader: ["orders"],
    folder: "orders",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 20 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // the contract a manager issues for signing (PDF of the terms or a scan)
  "contract-file": {
    visibility: "private",
    uploader: ["contracts"],
    folder: "contracts",
    types: ["application/pdf", "image/jpeg", "image/png", "image/webp"],
    maxBytes: 20 * MiB,
    typesLabel: "PDF یا تصویر (JPG، PNG، WebP)",
  },
  // photos or scans a customer sends with a support ticket
  "ticket-file": {
    visibility: "private",
    uploader: "customer",
    folder: "tickets",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 10 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // the same, sent by support staff with a reply or an internal note
  "ticket-staff-file": {
    visibility: "private",
    uploader: ["tickets"],
    folder: "tickets",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 10 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // the customer's card-to-card or bank transfer receipt, checked by staff before confirming
  "payment-receipt": {
    visibility: "private",
    uploader: "customer",
    folder: "payments",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 10 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // the receipt of a card-to-card, transfer or card reader payment that staff record: the cashier's
  // desk payment, or a sales agent's deposit on a Lead
  "staff-payment-receipt": {
    visibility: "private",
    uploader: ["cashier", "leads", "pipeline"],
    folder: "payments",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 10 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // a sales quote's own file (signed or stamped PDF, scan) sent to the buyer's panel with it
  "proforma-file": {
    visibility: "private",
    uploader: ["leads", "pipeline", "orders"],
    folder: "proformas",
    types: ["application/pdf", "image/jpeg", "image/png", "image/webp"],
    maxBytes: 10 * MiB,
    typesLabel: "PDF یا تصویر (JPG، PNG، WebP)",
  },
  // anything colleagues pass each other in the team chat
  "team-file": {
    visibility: "private",
    uploader: "staff",
    folder: "team-chat",
    types: [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/zip",
      "text/plain",
      "text/csv",
      "audio/mpeg",
      "audio/mp4",
      "audio/ogg",
      "audio/webm",
      "video/mp4",
    ],
    maxBytes: 50 * MiB,
    typesLabel: "تصویر، PDF، Word، Excel، PowerPoint، ZIP، متن، صدا یا ویدیوی MP4",
  },
  // a photo or scan a website visitor sends in the live chat; small, and counted per chat
  "live-chat-file": {
    visibility: "private",
    uploader: "visitor",
    folder: "live-chat",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 8 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // the same, sent by the support desk
  "live-chat-staff-file": {
    visibility: "private",
    uploader: ["livechat"],
    folder: "live-chat",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 10 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // a sea shipment's contract, handover scans, bills of lading, customs papers and photos,
  // filed per stage by the shipping section; scans of several pages run as large as an order's
  "shipment-file": {
    visibility: "private",
    uploader: ["shipping"],
    folder: "shipping",
    types: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 20 * MiB,
    typesLabel: "تصویر (JPG، PNG، WebP) یا PDF",
  },
  // a photo of the wanted part, taken with a phone inquiry so the parts desk finds the right one
  "parts-photo": {
    visibility: "private",
    uploader: ["requests"],
    folder: "parts",
    types: ["image/jpeg", "image/png", "image/webp"],
    maxBytes: 10 * MiB,
    typesLabel: "JPG، PNG یا WebP",
  },
  // a staff member's signature image, stamped on proformas and contracts they sign in an approval route
  "staff-signature": {
    visibility: "private",
    uploader: "staff",
    folder: "staff-signatures",
    types: ["image/jpeg", "image/png", "image/webp"],
    maxBytes: 1 * MiB,
    typesLabel: "JPG، PNG یا WebP",
  },
} as const satisfies Record<UploadPurpose, UploadPolicy>;

export const isUploadPurpose = (value: string): value is UploadPurpose =>
  uploadPurposeKeys.some((key) => key === value);

const faSize = new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 1 });

/** ۱٫۵ مگابایت */
export function formatBytes(bytes: number) {
  if (bytes >= 1024 * MiB) return `${faSize.format(bytes / (1024 * MiB))} گیگابایت`;
  if (bytes >= MiB) return `${faSize.format(bytes / MiB)} مگابایت`;
  if (bytes >= 1024) return `${faSize.format(bytes / 1024)} کیلوبایت`;
  return `${faSize.format(bytes)} بایت`;
}

/**
 * Largest public image the optimizer will decode (~200 MB of pixels on a 2 GB VPS);
 * bigger ones are refused at upload instead of breaking on the site later
 */
export const MAX_IMAGE_PIXELS = 50_000_000;

/**
 * Stand-in photos (Unsplash originals) until photos of the sold models exist. The site
 * serves them from the public bucket, where `bun run storage:setup` copies them: the API
 * server cannot reach Unsplash, and the image optimizer only resizes what it can fetch.
 */
export const stockPhotos = [
  "photo-1487754180451-c456f719a1fc",
  "photo-1489824904134-891ab64532f1",
  "photo-1493238792000-8113da705763",
  "photo-1494905998402-395d579af36f",
  "photo-1494976388531-d1058494cdd8",
  "photo-1502877338535-766e1452684a",
  "photo-1503376780353-7e6692767b70",
  "photo-1503736334956-4c8f8e92946d",
  "photo-1511919884226-fd3cad34687c",
  "photo-1542362567-b07e54358753",
  "photo-1549317661-bd32c8ce0db2",
  "photo-1552519507-da3b142c6e3d",
  "photo-1553440569-bcc63803a83d",
  "photo-1555215695-3004980ad54e",
  "photo-1567818735868-e71b99932e29",
  "photo-1568605117036-5fe5e7bab0b7",
  "photo-1580273916550-e323be2ae537",
  "photo-1605559424843-9e4c228bf1c2",
  "photo-1606590526453-9b8ee4569a70",
  "photo-1619642751034-765dfdf7c58e",
  // تابان شیپ (apps/shipping): car carrier at sea, cars on deck, painted car deck, RoRo ramp, port cranes
  "photo-1620307623055-b3ed36b3f214",
  "photo-1522674149721-b0191358dc5c",
  "photo-1729508180134-51bf90e4a6d2",
  "photo-1738828221287-8f5c497d021e",
  "photo-1598203637341-69d7580a1507",
] as const;
export type StockPhoto = (typeof stockPhotos)[number];

/** Bucket key of a stand-in photo: one copy for every environment, so no S3_KEY_PREFIX */
export const stockPhotoKey = (id: StockPhoto) => `site/stock/${id}.jpg`;
export const stockPhotoUrl = (id: StockPhoto) =>
  `https://taban.s3.ir-thr-at1.arvanstorage.ir/${stockPhotoKey(id)}`;

/** Files above this go up in parts, each retried on its own */
export const MULTIPART_THRESHOLD = 16 * MiB;
/** S3 needs at least 5 MiB per part except the last; 8 MiB keeps a retry cheap */
export const PART_SIZE = 8 * MiB;

/** Byte ranges of each part, in order, for a file of `size` bytes */
export function partsFor(size: number): { partNumber: number; size: number }[] {
  if (size <= MULTIPART_THRESHOLD) return [{ partNumber: 1, size }];
  const count = Math.ceil(size / PART_SIZE);
  return Array.from({ length: count }, (_, index) => ({
    partNumber: index + 1,
    size: index === count - 1 ? size - PART_SIZE * index : PART_SIZE,
  }));
}

/** What the browser needs to send the bytes straight to S3 */
export type UploadSession = {
  id: string;
  /** one presigned PUT per part; a single part means a plain object PUT */
  parts: { partNumber: number; size: number; url: string }[];
  partSize: number;
  /** headers every PUT of this upload must carry exactly (they are signed); none for parts */
  headers: { "content-type"?: string; "x-amz-acl"?: "public-read" };
};

/** A stored file as the apps see it; `url` is empty for private files */
export type StoredFile = {
  id: string;
  purpose: UploadPurpose;
  name: string;
  type: string;
  size: number;
  url: string;
};
